import React, { createContext, useContext, useEffect, useState } from "react";
import {
  LOCATIONS as M_LOCS,
  LOCATION_DETAILS as M_DET,
  LOCATION_OFFERINGS as M_OFF,
  ROOM_IMAGES as M_RI,
  MEMBERSHIPS as M_MEM,
  TESTIMONIALS as M_TES,
  TEAM as M_TEAM,
} from "../mock/mockData";
import { PAGE_DEFAULTS } from "../mock/pageDefaults";

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext(null);
export const useSiteContent = () => useContext(Ctx);

// Combined location docs matching the backend shape. Used as fallback so the
// public site never breaks if the API is slow/unreachable.
const buildFallback = () =>
  M_LOCS.map((l) => ({
    ...l,
    ...(M_DET[l.id] || {}),
    memberships: (M_OFF[l.id] || {}).memberships || [],
    rooms: (M_OFF[l.id] || {}).rooms || [],
    roomImages: M_RI[l.id] || {},
  }));

const buildGroups = (locations) => {
  const order = [];
  const map = {};
  locations.forEach((l) => {
    const city = (l.city || "").split(",")[0].trim();
    if (!map[city]) { map[city] = []; order.push(city); }
    map[city].push({ name: l.name, id: l.id, acceptingMembers: l.acceptingMembers !== false });
  });
  return order.map((city) => ({
    city,
    items: map[city].slice().sort((a, b) => (a.acceptingMembers === b.acceptingMembers ? 0 : a.acceptingMembers ? -1 : 1)),
  }));
};

const fetchOr = (path, setter, isValid) => {
  fetch(`${API}${path}`)
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((data) => { if (isValid(data)) setter(data); })
    .catch(() => {});
};

export const SiteContentProvider = ({ children }) => {
  const [locations, setLocations] = useState(buildFallback);
  const [memberships, setMemberships] = useState(M_MEM);
  const [testimonials, setTestimonials] = useState(M_TES);
  const [team, setTeam] = useState(M_TEAM);
  const [pages, setPages] = useState(PAGE_DEFAULTS);

  const refresh = () => {
    fetchOr("/api/content/locations", setLocations, (d) => Array.isArray(d) && d.length);
    fetchOr("/api/content/memberships", setMemberships, (d) => Array.isArray(d) && d.length);
    fetchOr("/api/content/testimonials", setTestimonials, (d) => Array.isArray(d) && d.length);
    fetchOr("/api/content/team", setTeam, (d) => d && (d.leadership || d.members));
    fetchOr("/api/content/pages", (d) => setPages((p) => ({ ...p, ...d })), (d) => d && typeof d === "object");
  };

  useEffect(() => { refresh(); }, []);

  const byId = Object.fromEntries(locations.map((l) => [l.id, l]));

  const value = {
    locations,
    getLocation: (id) => byId[id],
    locationGroups: buildGroups(locations),
    memberships,
    testimonials,
    team,
    pages,
    getPage: (key) => pages[key] || PAGE_DEFAULTS[key] || {},
    refresh,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
